import * as Sentry from '@sentry/browser';
import {
  PersistQueryClientProvider,
  PersistedClient,
  Persister,
} from '@tanstack/react-query-persist-client';
import { createQueryClient } from 'client-utils';
import dayjs from 'dayjs';
import localforage from 'localforage';
import { includes, last } from 'lodash';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { PersistGate } from 'redux-persist/integration/react';
import { registerSW } from 'virtual:pwa-register';
import pjson from '../package.json';
import App from './App';
import { buildQueryErrorContext } from './app/sentryReporting';
import { sessionExpired, setIsLoading, setUser } from './app/slices/authSlice';
import store, { persistor } from './app/store';
import userManager from './app/userManager';

import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

import 'dayjs/locale/sv';
dayjs.locale('sv');
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

userManager.getUser().then((user) => {
  store.dispatch(setUser(user as any));
  store.dispatch(setIsLoading(false));
});

userManager.events.addUserLoaded((user) => {
  store.dispatch(setUser(user as any));
  store.dispatch(setIsLoading(false));
});

userManager.events.addUserUnloaded(() => {
  store.dispatch(sessionExpired());
});

const redirectToSignout = () => {
  if (window.location.pathname !== '/signout') {
    window.location.assign('/signout');
  }
};

userManager.events.addAccessTokenExpired(() => {
  store.dispatch(sessionExpired());
  redirectToSignout();
});

userManager.events.addUserSignedOut(() => {
  store.dispatch(sessionExpired());
  redirectToSignout();
});

const searchParams = new URLSearchParams(window.location.search);
if (searchParams.has('fornya')) {
  localStorage.setItem('fornya', '1');
}
if (includes(window.location.href, 'bekrafta_konto')) {
  const code = last(window.location.href.split('/'));
  const match = code?.match(
    '^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$',
  );
  if (match && code) {
    localforage.setItem('bekrafta_konto', code);
  }
}

const queryClient = createQueryClient({
  onError: (error, _context, queryName) => {
    if (import.meta.env.PROD) {
      Sentry.captureException(error, buildQueryErrorContext(error, queryName));
    }
  },
  onUnauthorized: () => {
    store.dispatch(sessionExpired());
  },
});

const createIDBPersister = (idbValidKey: string = 'reactQuery') => {
  return {
    persistClient: async (client: PersistedClient) => {
      await localforage.setItem(idbValidKey, client);
    },
    restoreClient: async () => {
      return await localforage.getItem<PersistedClient>(idbValidKey);
    },
    removeClient: async () => {
      await localforage.removeItem(idbValidKey);
    },
  } as Persister;
};

const persister = createIDBPersister(
  `mitt-malarenergi-${import.meta.env.VITE_APP_ME_ENV}`,
);

const OIDC_ROUTES = ['/callback', '/silent-renew', '/signout'];

// En SW-uppdatering laddar om sidan. Sker det under OIDC-redirecten är
// auktoriseringskoden redan förbrukad och inloggningen misslyckas.
const registerServiceWorker = () => {
  if (OIDC_ROUTES.includes(window.location.pathname)) {
    return;
  }

  registerSW({
    onRegisterError: (error) => {
      console.error('Service worker registration failed', error);
    },
  });
};

const container = document.getElementById('root');
const root = createRoot(container as HTMLElement);
if (window.location.pathname === '/silent-renew') {
  userManager.signinSilentCallback().catch((error) => {
    console.error('Error during silent renew:', error);
  });
} else {
  root.render(
    <React.StrictMode>
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={{
          persister,
          buster: pjson.version,
          // import.meta.env.VITE_APP_ME_ENV === 'localhost'
          //   ? '' + Date.now()
          //   : pjson.version,
        }}
      >
        <Provider store={store}>
          <PersistGate loading={null} persistor={persistor}>
            <BrowserRouter>
              <App />
            </BrowserRouter>
          </PersistGate>
        </Provider>
      </PersistQueryClientProvider>
    </React.StrictMode>,
  );

  registerServiceWorker();
}
